import {
  CheckPermissionsOptions,
  ChecksRequest,
  ChecksResult,
  OrganizationChecksRequest,
  OrganizationChecksResult,
  ProductChecksRequest,
  ProductChecksResult,
  SystemChecksRequest,
  SystemChecksResult,
  useGlobalAccess,
  WorkspaceChecksRequest,
  WorkspaceChecksResult,
} from '@modules/auth/providers/GlobalAccessCache/GlobalAccessCache';
import { useOrganization } from 'app/_providers/Organization';
import { useWorkspace } from 'app/_providers/WorkspaceProvider';

import { useEffect, useState } from 'react';
import { Product } from 'shared/src/types';

export type PermissionsHookResult<R> = {
  loading: boolean;
  error?: Error;
  permissions?: R;
};

export function useSystemPermissions<T extends string = string>(
  checks: readonly T[],
  options?: CheckPermissionsOptions,
): PermissionsHookResult<Record<T, boolean>> {
  const res = usePermissions<T>(
    [{ type: 'system', permissions: checks }],
    options,
  );

  return {
    ...res,
    permissions: res?.permissions?.[0]?.permissions,
  };
}

export function useOrganizationPermissions<T extends string = string>(
  checks: readonly T[],
  options?: CheckPermissionsOptions,
): PermissionsHookResult<Record<T, boolean>> {
  const { organizationId } = useOrganization();
  const shouldSkip = !organizationId;
  const orgChecks: OrganizationChecksRequest<T>[] = [{
    type: 'organization', permissions: checks, organization: organizationId,
  }];
  const res = usePermissions<T>(
    shouldSkip ? [] : orgChecks,
    options,
  );

  return {
    ...res,
    permissions: res?.permissions?.[0]?.permissions ?? undefined,
  };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function useWorkspacePermissions<T extends string = string>(
  checks: readonly T[],
  options?: CheckPermissionsOptions,
): PermissionsHookResult<Record<T, boolean>> {
  const { organizationId } = useOrganization();
  const { name: workspaceName } = useWorkspace();
  const shouldSkip = !organizationId || !workspaceName;
  const workspaceChecks: WorkspaceChecksRequest<T>[] = [
    { type: 'workspace', permissions: checks, organization: organizationId, workspace: workspaceName },
  ];
  const res = usePermissions<T>(
    shouldSkip ? [] : workspaceChecks,
    options,
  );

  return {
    ...res,
    permissions: res?.permissions?.[0]?.permissions ?? undefined,
  };
}

export interface ProductPermissionsCheck<T extends string = string> {
  product: Product;
  checks: T[];
}
export type ProductPermissionsResult<T extends string = string> = Record<Product, Record<T, boolean>>;

export function useProductPermissions<T extends string = string>(
  checkBatches: ProductPermissionsCheck<T>[],
  options?: CheckPermissionsOptions,
): PermissionsHookResult<ProductPermissionsResult<T>> {
  const { organizationId } = useOrganization();
  const { name: workspaceName } = useWorkspace();
  const shouldSkip = !organizationId || !workspaceName;
  const checks: ProductChecksRequest<T>[] = checkBatches.map((batch) => ({
    type: 'product',
    permissions: batch.checks,
    organization: organizationId,
    workspace: workspaceName,
    product: batch.product,
  }));

  const res = usePermissions<T>(
    shouldSkip ? [] : checks,
    options,
  );

  let result: ProductPermissionsResult | undefined = undefined;

  if (res.permissions) {
    result = Object.fromEntries(res.permissions.map(
      (perms) => ([perms.product, perms.permissions]),
    )) as ProductPermissionsResult<T>;
  }

  return {
    ...res,
    permissions: result,
  };
}

export function usePermissions<T extends string = string>(
  checks: SystemChecksRequest<T>[],
  options?: CheckPermissionsOptions
): PermissionsHookResult<SystemChecksResult<T>[]>;
export function usePermissions<T extends string = string>(
  checks: OrganizationChecksRequest<T>[],
  options?: CheckPermissionsOptions
): PermissionsHookResult<OrganizationChecksResult<T>[]>;
export function usePermissions<T extends string = string>(
  checks: WorkspaceChecksRequest<T>[],
  options?: CheckPermissionsOptions
): PermissionsHookResult<WorkspaceChecksResult<T>[]>;
export function usePermissions<T extends string = string>(
  checks: ProductChecksRequest<T>[],
  options?: CheckPermissionsOptions
): PermissionsHookResult<ProductChecksResult<T>[]>;
export function usePermissions<T extends string = string>(
  checks: ChecksRequest<T>[],
  options?: CheckPermissionsOptions
): PermissionsHookResult<ChecksResult<T>[]>;

export function usePermissions(
  checks: ChecksRequest<string>[],
  options: CheckPermissionsOptions = { consistency: 'standard' },
): PermissionsHookResult<ChecksResult<string>[]> {
  const checker = useGlobalAccess();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [permissions, setPermissions] = useState<ChecksResult[] | undefined>(undefined);

  useEffect(() => {
    let mounted = true;

    (async () => {
      if (checks.length === 0) {
        return;
      }
      setLoading(true);
      setError(undefined);
      setPermissions(undefined);

      try {
        const resp = await checker.checkPermissions(checks, options);

        if (mounted) {
          setPermissions(resp);
        }
      } catch (err) {
        if (mounted) {
          setError(err as Error);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [JSON.stringify(checks), JSON.stringify(options)]);

  return {
    loading, error, permissions,
  };
}
